import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PrimaryCTA } from "@/components/CTA";
import { SunArcs } from "@/components/SunArcs";
import { WHATSAPP_URL } from "@/lib/scroll";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0323d563-196e-40a2-ba97-47396801feaa/images/0cd9f65d8d38de9f97b42c468acb543abc6b7a782b0ffca376a8a9356eef5b1d.jpeg";

const H1_LINES = ["Il tuo immobile in Salento", "può rendere di più."];

const EASE = [0.22, 1, 0.36, 1];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: EASE, delay: 0.35 + i * 0.16 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="section-hero"
      className="relative flex min-h-screen items-center overflow-hidden bg-navy"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Villa di lusso con piscina affacciata sul mare nel Salento"
          className="h-[115%] w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/45 to-navy/10" />
      </motion.div>

      <SunArcs
        className="pointer-events-none absolute -bottom-24 right-[-6%] w-[560px] max-w-[80vw]"
        color="#C9A227"
        rings={6}
        spin
        opacity={0.28}
      />

      <motion.div style={{ y: overlayY }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-28 pb-16 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-gold"
          data-testid="hero-eyebrow"
        >
          Revenue Management &amp; Gestione Affitti Brevi · Salento
        </motion.p>

        <h1 className="max-w-4xl font-display text-5xl font-light leading-[1.02] tracking-tighter text-white sm:text-6xl md:text-7xl" data-testid="hero-title">
          {H1_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "text-gold" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg"
          data-testid="hero-subtitle"
        >
          Analisi, pricing dinamico, distribuzione, compliance e gestione operativa: dall'avvio della
          struttura alla gestione completa. Prima leggiamo i dati, poi decidiamo insieme. E gli account
          restano intestati a te.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <PrimaryCTA testId="hero-cta-primary" />
          <a
            data-testid="hero-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/70 hover:bg-white/10"
          >
            Scrivici su WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/60 sm:text-sm"
          data-testid="hero-trustbar"
        >
          <span>Attivo nel Salento dal 2012</span>
          <span className="text-gold">·</span>
          <span>Avvio, revenue e gestione end-to-end</span>
          <span className="text-gold">·</span>
          <span>Account sempre intestati al proprietario</span>
          <span className="text-gold">·</span>
          <span>Analisi iniziale gratuita e senza impegno</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
